import { useState, useCallback, useMemo, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { ButtonSet } from '../../Modal/ButtonSet';
import Button from '../../presentation/Button';

type LegendItem = {
  key: string;
  color: string;
  originalKey: string;
};

type ModalComponent = React.ComponentType<{
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}>;

type Props = {
  open: boolean;
  onClose: () => void;
  legendData: LegendItem[];
  colorOverrides: Record<string, string> | null;
  labelOverrides: Record<string, string> | null;
  insightsSection?: ReactNode;
  onSave: (colorOverrides: Record<string, string>, labelOverrides: Record<string, string>) => void;
  onReset?: () => void;
  ModalComponent: ModalComponent;
};

export const ChartKeyEditModal = ({
  open,
  onClose,
  legendData,
  colorOverrides,
  labelOverrides,
  insightsSection,
  onSave,
  onReset,
  ModalComponent
}: Props): React.JSX.Element => {
  const [editedColors, setEditedColors] = useState<Record<string, string>>(() => colorOverrides || {});
  const [editedLabels, setEditedLabels] = useState<Record<string, string>>(() => labelOverrides || {});
  const [hexInputValues, setHexInputValues] = useState<Record<string, string>>({});

  const handleLabelChange = useCallback((originalKey: string, newLabel: string) => {
    setEditedLabels(prev => ({ ...prev, [originalKey]: newLabel }));
  }, []);

  const handleLabelBlur = useCallback((originalKey: string) => {
    const currentLabel = editedLabels[originalKey];
    if (currentLabel !== undefined) {
      const trimmedLabel = currentLabel.trim();
      setEditedLabels(prev => ({ ...prev, [originalKey]: trimmedLabel }));
    }
  }, [editedLabels]);

  const handleHexInputChange = useCallback((originalKey: string, hexValue: string) => {
    setHexInputValues(prev => ({ ...prev, [originalKey]: hexValue }));
  }, []);

  const handleHexInputBlur = useCallback((originalKey: string) => {
    const hexValue = hexInputValues[originalKey];
    if (!hexValue) return;

    const cleanValue = hexValue.replace('#', '').toUpperCase();

    if (/^[0-9A-F]{6}$/i.test(cleanValue)) {
      const hexColor = `#${cleanValue}`;
      setEditedColors(prev => ({ ...prev, [originalKey]: hexColor }));
      setHexInputValues(prev => {
        const updated = { ...prev };
        delete updated[originalKey];
        return updated;
      });
    } else {
      setHexInputValues(prev => {
        const updated = { ...prev };
        delete updated[originalKey];
        return updated;
      });
    }
  }, [hexInputValues]);

  const handleSave = useCallback(() => {
    const hasInvalidLabels = Object.values(editedLabels).some(
      label => label !== undefined && label.trim().length === 0
    );
    if (hasInvalidLabels) return;
    onSave(editedColors, editedLabels);
    onClose();
  }, [editedColors, editedLabels, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setEditedColors(colorOverrides || {});
    setEditedLabels(labelOverrides || {});
    setHexInputValues({});
    onClose();
  }, [colorOverrides, labelOverrides, onClose]);

  // const handleReset = useCallback(() => {
  //   if (onReset) {
  //     setEditedColors({});
  //     setEditedLabels({});
  //     setHexInputValues({});
  //     onReset();
  //     onClose();
  //   }
  // }, [onReset, onClose]);

  const displayData = useMemo(() => {
    return legendData.map(item => ({
      ...item,
      displayColor: editedColors[item.originalKey] ?? item.color,
      displayLabel: editedLabels[item.originalKey] ?? item.key,
      hexInputValue: hexInputValues[item.originalKey] ?? (editedColors[item.originalKey] ?? item.color).replace('#', '').toUpperCase(),
    }));
  }, [legendData, editedColors, editedLabels, hexInputValues]);

  const canSave = !Object.values(editedLabels).some(
    label => label !== undefined && label.trim().length === 0
  );

  return (
    <ModalComponent open={open} onClose={handleCancel}>
      <ModalRoot>
        <ModalHeader>Edit Chart</ModalHeader>

        <LegendList>
          {displayData.map((item) => (
            <LegendRow key={item.originalKey}>
              <ColorSwatch style={{ backgroundColor: item.displayColor }} />
              <ColorInputMain>
                <ColorInputPrefix>#</ColorInputPrefix>
                <ColorInputField
                  type="text"
                  value={item.hexInputValue}
                  onChange={(e) => handleHexInputChange(item.originalKey, e.target.value)}
                  onBlur={() => handleHexInputBlur(item.originalKey)}
                  maxLength={6}
                />
              </ColorInputMain>
              <LabelInput
                type="text"
                value={item.displayLabel}
                onChange={(e) => handleLabelChange(item.originalKey, e.target.value)}
                onBlur={() => handleLabelBlur(item.originalKey)}
                placeholder="Enter label"
              />
            </LegendRow>
          ))}
        </LegendList>

        {insightsSection && <InsightsSlot>{insightsSection}</InsightsSlot>}

        <Footer>
          <Button.Destructive variant="brick" onClick={handleCancel}>
            Cancel
          </Button.Destructive>
          <Button.Primary variant="brick" onClick={handleSave} disabled={!canSave}>
            Save
          </Button.Primary>
        </Footer>
      </ModalRoot>
    </ModalComponent>
  );
};

ChartKeyEditModal.displayName = 'ChartKeyEditModal';

// Styled Components

// The host Modal already supplies the width (600px) and 20px padding, so this just fills it. Only
// the legend list scrolls; the header, insights and footer stay put (the host scrolls as a last
// resort when the insights editor is tall).
const ModalRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
});

const ModalHeader = styled('h2')({
  margin: '0 0 20px 0',
  fontFamily: 'var(--font-bold)',
  fontSize: '22px',
  color: 'var(--black, #1a1a1a)',
  textAlign: 'center',
});

const LegendList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  maxHeight: 'min(340px, 40vh)',
  overflowY: 'auto',
  overflowX: 'hidden',
  // Keep the rows clear of the scrollbar.
  paddingRight: '8px',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
});

const LegendRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
  minWidth: 0,
});

const ColorSwatch = styled('div')({
  height: '41px',
  width: '41px',
  borderRadius: '4px',
  flexShrink: 0,
});

const ColorInputMain = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  width: '120px',
  padding: '10px 5px 10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'text',
  '&:focus-within': { borderColor: 'var(--gray-m, #999)' },
});

const ColorInputPrefix = styled('span')({
  fontSize: '16px',
  color: '#333',
});

const ColorInputField = styled('input')({
  fontFamily: 'var(--font-reg, inherit)',
  border: 'none',
  fontSize: '16px',
  minWidth: 0,
  width: '100%',
  paddingRight: '5px',
  textTransform: 'uppercase',
  background: 'transparent',
  '&:focus': { outline: 'none' },
  '&::selection': { backgroundColor: 'var(--blue-l, #b3d9ff)' },
});

const LabelInput = styled('input')({
  flex: 1,
  minWidth: 0,
  padding: '10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  fontSize: '16px',
  fontFamily: 'var(--font-reg, inherit)',
  boxSizing: 'border-box',
  '&:focus': { outline: 'none', borderColor: 'var(--gray-m, #999)' },
  '&::placeholder': { color: 'var(--gray-l, #ccc)' },
});

// Separates the insights editor from the legend list above it. Only rendered when there is an
// insights section, so a modal without one doesn't get a stray gap.
const InsightsSlot = styled('div')({
  marginTop: '24px',
});

const Footer = styled(ButtonSet)({
  flexShrink: 0,
  paddingTop: '20px',
});
